import moment from "moment";

import { DateFormats } from "@enums/shared.enum";
import { ResourcePricesResponse } from "@models/components/booking.model";
import { BookingDetailsType } from "@models/components/user.model";
import {
  AvailableTiming,
  BackToBackBookingSlot,
  BookingSliceInitial,
  SingleSlot,
  timeMapObj,
} from "@models/services/booking";
import { AddGuestType } from "@models/shared/shared.model";
import { CaseReducer, createSlice, PayloadAction } from "@reduxjs/toolkit";

import drawerSlice from "./drawer.slice";

const initialState: BookingSliceInitial = {
  amenityAddedUnavailableError: false,
  amenityConflictError: false,
  amenityPriceChangedError: false,
  amenityUnavailableError: false,
  backToBackBookingEnabled: false,
  backToBackBookingSlots: [],
  bookingDataChanged: false,
  bookingEditData: {} as BookingDetailsType,
  calenderStartDate: moment().format(DateFormats.YMD),
  checkparticipantErrors: {},
  editBookingWarning: {
    date: false,
    duration: false,
    location: false,
    resource: false,
    time: false,
  },
  gdprAccepted: false,
  guestList: [],
  isAmenityEdit: false,
  isEditBooking: false,
  isEditBookingDataLoaded: false,
  isSingleTimeSlot: false,
  legalTermsAccepted: false,
  paymentLoader: false,
  priceLoader: false,
  resourcePrices: {} as ResourcePricesResponse,
  savedMethodMissingError: false,
  selectedAmenitie: [],
  selectedDate: moment().format(DateFormats.YMD),
  selectedSlot: {} as SingleSlot,
  selectedTime: "",
  selectedTokenizationId: null,
  showPaymentCancelWarning: false,
  timeMaps: [],
};

type bookingSliceReducer<Payload> = CaseReducer<
  BookingSliceInitial,
  PayloadAction<Payload>
>;

const changeCalenderStartDate: bookingSliceReducer<string> = (
  state,
  action
) => {
  state.calenderStartDate = action.payload;
};

const resetBookingData: bookingSliceReducer<null> = state => {
  state.selectedTime = "";
  state.selectedSlot = {} as SingleSlot;
  state.selectedTokenizationId = null;
  state.guestList = [];
  state.amenityConflictError = false;
  state.amenityPriceChangedError = false;
  state.amenityUnavailableError = false;
  state.amenityAddedUnavailableError = false;
};

const changeSelectedDate: bookingSliceReducer<string> = (state, action) => {
  state.selectedDate = action.payload;
  state.selectedSlot = {} as SingleSlot;
  state.selectedTime = "";
  state.gdprAccepted = false;
  state.legalTermsAccepted = false;
  state.resourcePrices = {} as ResourcePricesResponse;
};

const updateTimeMaps: bookingSliceReducer<Array<Array<timeMapObj>>> = (
  state,
  action
) => {
  state.timeMaps = action.payload;
};

const changeIsSingleTimeSlot: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.isSingleTimeSlot = action.payload;
};

const acceptGdpr: bookingSliceReducer<boolean> = (state, action) => {
  state.gdprAccepted = action.payload;
};

const acceptLegalTerms: bookingSliceReducer<boolean> = (state, action) => {
  state.legalTermsAccepted = action.payload;
};

const changeSelectedTime: bookingSliceReducer<string> = (state, action) => {
  state.selectedSlot = {} as SingleSlot;
  state.selectedTime = action.payload;
  state.gdprAccepted = false;
  state.legalTermsAccepted = false;
};

const changeSelectedSlot: bookingSliceReducer<{
  slot: AvailableTiming;
  resourceId: string;
  isBookable: boolean;
}> = (state, action) => {
  state.selectedSlot = action.payload;
  state.gdprAccepted = false;
  state.legalTermsAccepted = false;
};
const setCheckParticipantErrors: bookingSliceReducer<Record<string, string>> = (
  state,
  action
) => {
  state.checkparticipantErrors = action.payload;
};

const setResourcePrices: bookingSliceReducer<ResourcePricesResponse> = (
  state,
  action
) => {
  state.resourcePrices = action.payload;
};

const clearStaleAmenities: bookingSliceReducer<null> = state => {
  // Do not clear newly-added amenities when changing slot/resource/date/time.
  // We allow them to persist so the user can see them and remove if needed (conflict logic handles warnings).
  // state.selectedAmenitie = state.isEditBooking
  //   ? state.selectedAmenitie.filter(sa => sa.isBooked)
  //   : [];
  state.isAmenityEdit = false;
};
const flush: bookingSliceReducer<null> = state => {
  state.selectedDate = moment().format(DateFormats.YMD);
  state.selectedSlot = {} as SingleSlot;
  state.selectedTime = "";
  state.selectedTokenizationId = null;
  state.calenderStartDate = moment().format(DateFormats.YMD);
  state.gdprAccepted = false;
  state.legalTermsAccepted = false;
  state.resourcePrices = {} as ResourcePricesResponse;
  state.paymentLoader = false;
  state.priceLoader = false;
  state.isAmenityEdit = false;
  state.isEditBooking = false;
  state.isEditBookingDataLoaded = false;
  state.bookingEditData = {} as BookingDetailsType;
  state.bookingDataChanged = false;
  state.selectedAmenitie = [];
  state.guestList = [];
  state.amenityConflictError = false;
  state.amenityPriceChangedError = false;
  state.amenityUnavailableError = false;
  state.amenityAddedUnavailableError = false;
  state.showPaymentCancelWarning = false;
};

const setSelectedTokenizationId: bookingSliceReducer<string | number | null> = (
  state,
  action
) => {
  state.selectedTokenizationId = action.payload;
  if (action.payload) {
    state.showPaymentCancelWarning = false;
  }
};

const setShowPaymentCancelWarning: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.showPaymentCancelWarning = action.payload;
  state.backToBackBookingEnabled = false;
  state.backToBackBookingSlots = [];
};

const setPaymentLoader: bookingSliceReducer<boolean> = (state, action) => {
  state.paymentLoader = action.payload;
};
const setPriceLoader: bookingSliceReducer<boolean> = (state, action) => {
  state.priceLoader = action.payload;
};
const addGuest: bookingSliceReducer<AddGuestType> = (state, action) => {
  const exists = state.guestList.some(
    p => p.profileUuid === action.payload.profileUuid
  );
  if (!exists) state.guestList.push(action.payload);
};

const removeGuest: bookingSliceReducer<string> = (state, action) => {
  state.guestList = state.guestList.filter(
    p => p.profileUuid !== action.payload
  );
};

const setGuests: bookingSliceReducer<AddGuestType[]> = (state, action) => {
  state.guestList = action.payload;
};
const setIsEditBooking: bookingSliceReducer<boolean> = (state, action) => {
  state.isEditBooking = action.payload;
  state.amenityConflictError = false;
  state.amenityPriceChangedError = false;
  state.amenityUnavailableError = false;
  state.amenityAddedUnavailableError = false;
};
const setIsEditBookingDataLoaded: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.isEditBookingDataLoaded = action.payload;
};

const setBookingEditData: bookingSliceReducer<BookingDetailsType> = (
  state,
  action
) => {
  state.bookingEditData = action.payload;
};
const setEditBookingWarning: bookingSliceReducer<
  Partial<BookingSliceInitial["editBookingWarning"]>
> = (state, action) => {
  state.editBookingWarning = {
    ...state.editBookingWarning,
    ...action.payload,
  };
};

const setBackToBackBookingEnabled: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.backToBackBookingEnabled = action.payload;
};

const setBackToBackBookingSlots: bookingSliceReducer<
  BackToBackBookingSlot[]
> = (state, action) => {
  state.backToBackBookingSlots = action.payload;
};

const setBookingDataChanged: bookingSliceReducer<boolean> = (state, action) => {
  state.bookingDataChanged = action.payload;
};

const setSelectedAmenities: bookingSliceReducer<
  BookingSliceInitial["selectedAmenitie"]
> = (state, action) => {
  state.selectedAmenitie = action.payload;
};

const setIsAmenityEdit: bookingSliceReducer<boolean> = (state, action) => {
  state.isAmenityEdit = action.payload;
};

const setAmenityConflictError: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.amenityConflictError = action.payload;
};

const setAmenityPriceChangedError: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.amenityPriceChangedError = action.payload;
};

const setAmenityUnavailableError: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.amenityUnavailableError = action.payload;
};

const setAmenityAddedUnavailableError: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.amenityAddedUnavailableError = action.payload;
};

const setSavedMethodMissingError: bookingSliceReducer<boolean> = (
  state,
  action
) => {
  state.savedMethodMissingError = action.payload;
};

const bookingSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(drawerSlice.actions.removeDrawerBookingState, state => {
      state.selectedTime = "";
      state.selectedSlot = {} as SingleSlot;

      if (state.guestList.length > 0) {
        state.guestList = [];
      }
    });
  },
  initialState,
  name: "booking",
  reducers: {
    acceptGdpr,
    acceptLegalTerms,
    addGuest,
    changeCalenderStartDate,
    changeIsSingleTimeSlot,
    changeSelectedDate,
    changeSelectedSlot,
    changeSelectedTime,
    clearStaleAmenities,
    flush,
    removeGuest,
    resetBookingData,
    setAmenityAddedUnavailableError,
    setAmenityConflictError,
    setAmenityPriceChangedError,
    setAmenityUnavailableError,
    setBackToBackBookingEnabled,
    setBackToBackBookingSlots,
    setBookingDataChanged,
    setBookingEditData,
    setCheckParticipantErrors,
    setEditBookingWarning,
    setGuests,
    setIsAmenityEdit,
    setIsEditBooking,
    setIsEditBookingDataLoaded,
    setPaymentLoader,
    setPriceLoader,
    setResourcePrices,
    setSavedMethodMissingError,
    setSelectedAmenities,
    setSelectedTokenizationId,
    setShowPaymentCancelWarning,
    updateTimeMaps,
  },
});

export default bookingSlice;
